/**
 * Theme Context
 * Manages light/dark mode theme state with:
 * - localStorage persistence
 * - Automatic time-based switching
 * - Configurable timezone and switch times
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

type Theme = 'light' | 'dark';
type ThemeMode = 'manual' | 'auto';

interface ThemeSettings {
  mode: ThemeMode;
  timezone: string;
  lightStartHour: number; // Hour (0-23) when light mode should start
  darkStartHour: number;  // Hour (0-23) when dark mode should start
}

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
  // Settings
  settings: ThemeSettings;
  setMode: (mode: ThemeMode) => void;
  setTimezone: (timezone: string) => void;
  setLightStartHour: (hour: number) => void;
  setDarkStartHour: (hour: number) => void;
  availableTimezones: string[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'resova-theme';
const SETTINGS_KEY = 'resova-theme-settings';

// Default settings: dark mode by default (manual), can switch to auto mode for time-based
const DEFAULT_SETTINGS: ThemeSettings = {
  mode: 'manual',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  lightStartHour: 7,  // 7:00 AM
  darkStartHour: 19,  // 7:00 PM
};

// Common timezones for the dropdown
const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Dublin',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Pacific/Auckland',
];

function getThemeForTime(settings: ThemeSettings): Theme {
  // Get current hour in the specified timezone
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: settings.timezone,
  });
  const currentHour = parseInt(formatter.format(now), 10);

  // Check if current time falls within light mode hours
  const { lightStartHour, darkStartHour } = settings;

  if (lightStartHour < darkStartHour) {
    // Normal case: light during day (e.g., 7am-7pm)
    return currentHour >= lightStartHour && currentHour < darkStartHour ? 'light' : 'dark';
  } else {
    // Inverted case: light during night (e.g., 7pm-7am)
    return currentHour >= lightStartHour || currentHour < darkStartHour ? 'light' : 'dark';
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Load settings from localStorage
  const [settings, setSettings] = useState<ThemeSettings>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SETTINGS_KEY);
      if (stored) {
        try {
          return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
        } catch {
          return DEFAULT_SETTINGS;
        }
      }
    }
    return DEFAULT_SETTINGS;
  });

  // Load theme from localStorage or compute from time
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      // If we have a stored theme and mode is manual, use it
      if (stored === 'light' || stored === 'dark') {
        const storedSettings = localStorage.getItem(SETTINGS_KEY);
        if (storedSettings) {
          try {
            const parsed = JSON.parse(storedSettings);
            if (parsed.mode === 'manual') {
              return stored;
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
    // Default to dark or compute based on time
    return settings.mode === 'auto' ? getThemeForTime(settings) : 'dark';
  });

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light');
    } else {
      root.classList.remove('light');
    }
    // Persist to localStorage
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  // Auto-switch theme based on time
  useEffect(() => {
    if (settings.mode !== 'auto') return;

    const checkAndUpdateTheme = () => {
      const newTheme = getThemeForTime(settings);
      setThemeState(newTheme);
    };

    // Check immediately
    checkAndUpdateTheme();

    // Check every minute for time-based changes
    const interval = setInterval(checkAndUpdateTheme, 60 * 1000);

    return () => clearInterval(interval);
  }, [settings]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    // If manually setting theme, switch to manual mode
    if (settings.mode === 'auto') {
      setSettings(prev => ({ ...prev, mode: 'manual' }));
    }
  }, [settings.mode]);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
    // Switch to manual mode when toggling
    if (settings.mode === 'auto') {
      setSettings(prev => ({ ...prev, mode: 'manual' }));
    }
  }, [settings.mode]);

  const setMode = useCallback((mode: ThemeMode) => {
    setSettings(prev => ({ ...prev, mode }));
    if (mode === 'auto') {
      // Immediately apply the time-based theme
      setThemeState(getThemeForTime({ ...settings, mode }));
    }
  }, [settings]);

  const setTimezone = useCallback((timezone: string) => {
    setSettings(prev => {
      const newSettings = { ...prev, timezone };
      if (prev.mode === 'auto') {
        setThemeState(getThemeForTime(newSettings));
      }
      return newSettings;
    });
  }, []);

  const setLightStartHour = useCallback((hour: number) => {
    setSettings(prev => {
      const newSettings = { ...prev, lightStartHour: hour };
      if (prev.mode === 'auto') {
        setThemeState(getThemeForTime(newSettings));
      }
      return newSettings;
    });
  }, []);

  const setDarkStartHour = useCallback((hour: number) => {
    setSettings(prev => {
      const newSettings = { ...prev, darkStartHour: hour };
      if (prev.mode === 'auto') {
        setThemeState(getThemeForTime(newSettings));
      }
      return newSettings;
    });
  }, []);

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
    isDark: theme === 'dark',
    settings,
    setMode,
    setTimezone,
    setLightStartHour,
    setDarkStartHour,
    availableTimezones: COMMON_TIMEZONES,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
